/**
 * WorkflowProcessFields - Campos gerais do processo (Workflow 2.0)
 *
 * Formulário compartilhado entre o modal de criação ("Novo processo") e o
 * modal de configurações, garantindo que os dois tenham exatamente os mesmos
 * campos e o mesmo padrão visual do sheet do construtor ("Sidebar produtiva"):
 * cartões de seção, rótulos compactos e explicações no botão "?".
 *
 * Camada de apresentação apenas — a escrita continua sendo feita pelo
 * componente que usa este formulário, via RPC segura (`upsertDefinition`).
 *
 * @version 1.1.0
 * RELEASE: 2026-09-13 v1.1.0 - Escopo por workspace exibido apenas para a
 *          entidade Negócio (Tarefa/Retenção/Jurídico/Financeiro não usam
 *          workspaces do CRM).
 * RELEASE: 2026-08-31 v1.0.0 - Criação (padroniza criação x edição do processo)
 */

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SectionCard, CompactLabel } from './inspector/SectionCard';

/** Explicações longas centralizadas (exibidas nos botões "?") */
export const AJUDA_ENTIDADE_PRINCIPAL =
  'É o registro sobre o qual a esteira roda — cada execução acompanha um registro desse tipo e não pode ser trocada depois de criado o processo. O evento que faz o processo iniciar (negócio criado, proposta aceita, contrato assinado...) é escolhido no bloco Início, dentro do construtor. Eventos de outros módulos (proposta, contrato, financeiro) continuam disponíveis como gatilho porque estão ligados a esse mesmo registro.';

export const AJUDA_POLITICA_BLOQUEIO =
  'Só acompanhar: registra o andamento sem impedir nada. Avisar: alerta quando algo acontece fora da ordem prevista. Bloquear: impede a ação quando ela fere a ordem do processo.';

export const AJUDA_ESCOPO_WORKSPACE =
  'Limita o processo aos workspaces marcados. Sem nenhum marcado, o processo vale para todos os workspaces do tenant.';

export interface WorkspaceOpcao {
  id: string;
  name: string;
}

export interface EntidadeOpcao {
  code: string;
  label: string;
}

interface Props {
  nome: string;
  onNomeChange: (valor: string) => void;
  descricao: string;
  onDescricaoChange: (valor: string) => void;
  /** Código da entidade principal (raiz das execuções) */
  entidade: string;
  /** Quando ausente, a entidade fica somente leitura (edição do processo) */
  onEntidadeChange?: (valor: string) => void;
  entidades: EntidadeOpcao[];
  enforcement: string;
  onEnforcementChange: (valor: string) => void;
  workspaceIds: string[];
  onToggleWorkspace: (id: string) => void;
  workspaces: WorkspaceOpcao[];
  /** Desabilita todos os campos (sem permissão de edição) */
  disabled?: boolean;
}

export function WorkflowProcessFields({
  nome,
  onNomeChange,
  descricao,
  onDescricaoChange,
  entidade,
  onEntidadeChange,
  entidades,
  enforcement,
  onEnforcementChange,
  workspaceIds,
  onToggleWorkspace,
  workspaces,
  disabled,
}: Props) {
  const entidadeSomenteLeitura = !onEntidadeChange;
  // v1.1.0: escopo por workspace só existe para processos de Negócio (CRM)
  const mostrarEscopo = entidade === 'crm_deal';
  const rotuloEntidade = entidades.find((e) => e.code === entidade)?.label ?? entidade;

  return (
    <div className="space-y-3">
      {/* ------------------------------------------------ Identificação */}
      <SectionCard title="Identificação">
        <div className="space-y-1.5">
          <CompactLabel>Nome</CompactLabel>
          <Input
            className="h-8 text-sm"
            value={nome}
            maxLength={120}
            disabled={disabled}
            onChange={(e) => onNomeChange(e.target.value.slice(0, 120))}
          />
        </div>
        <div className="space-y-1.5">
          <CompactLabel help="Texto livre para a equipe entender o objetivo desta esteira.">
            Descrição
          </CompactLabel>
          <Textarea
            className="min-h-[64px] text-sm"
            rows={3}
            value={descricao}
            maxLength={500}
            disabled={disabled}
            onChange={(e) => onDescricaoChange(e.target.value.slice(0, 500))}
          />
        </div>
      </SectionCard>

      {/* ----------------------------------------------------- Execução */}
      <SectionCard title="Execução">
        <div className="space-y-1.5">
          <CompactLabel help={AJUDA_ENTIDADE_PRINCIPAL}>Entidade principal</CompactLabel>
          {entidadeSomenteLeitura ? (
            <Input className="h-8 text-sm" value={rotuloEntidade} readOnly disabled />
          ) : (
            <Select value={entidade} onValueChange={onEntidadeChange} disabled={disabled}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {entidades.map((e) => (
                  <SelectItem key={e.code} value={e.code}>{e.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="space-y-1.5">
          <CompactLabel help={AJUDA_POLITICA_BLOQUEIO}>Política padrão de bloqueio</CompactLabel>
          <Select value={enforcement} onValueChange={onEnforcementChange} disabled={disabled}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="allow">Só acompanhar (não bloqueia)</SelectItem>
              <SelectItem value="warn">Avisar quando fora da ordem</SelectItem>
              <SelectItem value="block">Bloquear quando fora da ordem</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </SectionCard>

      {/* -------------------------------------------------------- Escopo
          v1.1.0: workspace é estrutura exclusiva do CRM. Entidades de Tarefa,
          Retenção, Jurídico e Financeiro não exibem esta seção. */}
      {mostrarEscopo && (
      <SectionCard title="Escopo por workspace" help={AJUDA_ESCOPO_WORKSPACE}>
        {/* ScrollArea precisa de altura fixa: com `max-h` o viewport do Radix
            não rola e a lista fica presa (correção do problema relatado). */}
        <ScrollArea className="h-44 w-full rounded-md border bg-background p-2">
          <div className="space-y-2 pr-2">
            {workspaces.map((w) => (
              <label key={w.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={workspaceIds.includes(w.id)}
                  disabled={disabled}
                  onCheckedChange={() => onToggleWorkspace(w.id)}
                />
                <span className="truncate">{w.name}</span>
              </label>
            ))}
            {workspaces.length === 0 && (
              <p className="text-xs text-muted-foreground">Nenhum workspace disponível.</p>
            )}
          </div>
        </ScrollArea>
      </SectionCard>
      )}
    </div>
  );
}
